import { Analytics } from '@evertel/analytics'
import { useService } from '@evertel/di'
import { IdleTimerService } from '@evertel/web/core'
import { Checkbox, FloatingMenu, Icon, InfoBox, MenuHeader, MenuItem, Modal, ModalBody, ModalFooter, ModalHeader, Button, Text, Badge } from '@evertel/web/ui'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'

type TimeoutOption = {
    value: number | null
    label: string
    description: string
}

const TIMEOUT_OPTIONS: TimeoutOption[] = [
    {
        value: 30,
        label: '30 minutes (Default)',
        description: 'Standard CJIS-compliant timeout'
    },
    {
        value: 480,
        label: '8 hours',
        description: 'Logs out after 8h of inactivity'
    },
    {
        value: null,
        label: 'Disable auto-logout',
        description: 'For secure, staffed facilities only'
    }
]

type HeaderSessionTimeoutProps = React.ButtonHTMLAttributes<HTMLButtonElement>

const HeaderSessionTimeout: React.FC<HeaderSessionTimeoutProps> = observer(({
    className,
    ...otherProps
}) => {

    const analytics = useService(Analytics)
    const idleTimerService = useService(IdleTimerService)

    const [pendingTimeout, setPendingTimeout] = useState<number | null>(null)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

    const getTimeoutLabel = (minutes: number | null): string => {
        if (minutes === null) return 'disabled'
        if (minutes < 60) return `${minutes} minutes`
        const hours = Math.floor(minutes / 60)
        return `${hours} hour${hours > 1 ? 's' : ''}`
    }

    const getTimeoutBadgeLabel = (minutes: number | null) => {
        if (minutes === null) return (
            <span style={{fontSize: 'large'}} className={'text-danger'}>∞</span>
        )
        // if (minutes < 60) return `${minutes}m`
        if (minutes < 60) return ``
        const hours = Math.floor(minutes / 60)
        return `${hours}h`
    }

    const handleTimeoutClick = (value: number | null) => {
        if (value === idleTimerService.timeoutMinutes) {
            return
        }

        if (!value || value > 30) { // ∞ || 8h
            setPendingTimeout(value)
            setIsConfirmModalOpen(true)
        } else {
            //can't call handleConfirm directly or pendingTimeout isn't updated
            idleTimerService.setTimeoutMinutes(value)
        }

        analytics.logView('/session-timeout/confirm', true)
    }

    const handleConfirm = async () => {
        await idleTimerService.setTimeoutMinutes(pendingTimeout)

        setIsConfirmModalOpen(false)
        setPendingTimeout(null)
    }

    const handleCancel = () => {
        setIsConfirmModalOpen(false)
        setPendingTimeout(null)
    }

    return (
        <>
            <FloatingMenu
                id="header-dropdown-session-timeout-menu"
                label="Session Timeout Menu"
                title="Session Timeout Menu"
                aria-label="Session Timeout Menu"
                renderLabel={() => (
                    <div className="position-relative">
                        <Icon
                            className="d-block"
                            name="timer"
                            size={16}
                            style={{ width: 20, textAlign: 'center' }}
                        />
                        <Badge
                            size={12}
                            shape="rounded-pill"
                            className="p-0 position-absolute top-100  translate-middle"
                        >
                            {getTimeoutBadgeLabel(idleTimerService.timeoutMinutes)}
                        </Badge>
                    </div>
                )}
                className={className}
                {...otherProps}
            >
                <MenuHeader label="Session Timeout Menu" />
                {TIMEOUT_OPTIONS.map((option) => (
                    <MenuItem
                        key={option.value ?? 'disabled'}
                        label={option.label}
                        onClick={() => handleTimeoutClick(option.value)}
                        className="d-flex flex-row"
                    >
                        <Checkbox
                            radio
                            name="timeout-option"
                            checked={idleTimerService.timeoutMinutes === option.value}
                        />
                        <div className="d-flex flex-column ms-2">
                            <span>{option.label}</span>
                            <small className="text-muted">{option.description}</small>
                        </div>
                    </MenuItem>
                ))}
            </FloatingMenu>

            <Modal
                visible={isConfirmModalOpen}
                onClose={handleCancel}
                size="md"
            >
                <ModalHeader title="Confirm Session Timeout Change" />
                <ModalBody>
                    {pendingTimeout === null ? (
                        <>
                            <InfoBox color="warning" className="mb-4">
                                <div className="mb-3">
                                    <strong>Important:</strong> You may only disable the automatic logout timer if ALL of the following conditions are met:
                                </div>
                                <ul className="mb-3 ps-3">
                                    <li className="mb-2">You are operating from a physically secure location (dispatch center or criminal justice facility)</li>
                                    <li className="mb-2">Your workstation is located within a staffed facility that remains attended during your session</li>
                                    <li className="mb-2">Your terminal is designated for receiving alert notifications or performing dispatch functions</li>
                                </ul>
                            </InfoBox>

                            <div className="mb-3">
                                <p>Your session timeout will be <strong>disabled</strong>.</p>
                            </div>

                        </>
                    ) : (
                        <>
                            <InfoBox color="warning" className="mb-4">
                                <div className="mb-3">
                                    <strong>Important:</strong> You may only extend the automatic logout timer if ALL of the following conditions are met:
                                </div>
                                <ul className="mb-3 ps-3">
                                    <li className="mb-2">You are operating from a physically secure location (dispatch center or criminal justice facility)</li>
                                    <li className="mb-2">Your workstation is located within a staffed facility that remains attended during your session</li>
                                    <li className="mb-2">Your terminal is designated for receiving alert notifications or performing dispatch functions</li>
                                </ul>
                            </InfoBox>

                            <div className="mb-3">
                                <p>Your session timeout will be set to <strong>{getTimeoutLabel(pendingTimeout)}</strong>.</p>
                            </div>

                        </>
                    )}
                </ModalBody>
                <ModalFooter className="text-end">
                    <Button
                        color="link"
                        onClick={handleCancel}
                        className="me-2">
                        <Text>Cancel</Text>
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleConfirm}>
                        <Text>Confirm</Text>
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
})

export { HeaderSessionTimeout }
